import * as Enumerable from 'linq';
import { EmbeddedCode, CodeStatus, CodeInvoiceStatus, CatalogueCode, ToEmbeddedCode } from '../entities/shared/code';
import { LabourCodeId, FinanceSettings, PrepaymentSetting, PrepaymentCodeId, PrepaymentCodeName } from '../entities/settings/general.settings';
import { TimesheetEntry } from '../entities/time.billing/timesheet.entry';
import { ClaimItem } from '../entities/claims/claim.item';
import { WorkOrderUtility } from './workorder.utility';
import { Source } from '../enums/enum.source';
import Decimal from 'decimal.js';
import { v4 } from 'uuid';

export interface TotalCodeInfo {
    totalSell: number;
    totalBuy: number;
    totalCodes: number;
}

export class CodeUtility {

    public static expandUploadedCode(code: CatalogueCode, excludeCancelled: boolean = true): EmbeddedCode[] {

        let builtCodes: EmbeddedCode[] = [];

        switch (code.type) {
            // SOR is an atomic line like SERVICE — it never expands into children.
            // This switch has no default, so a missing case silently drops the code.
            case 'SERVICE':
            case 'INVENTORY':
            case 'NON-INVENTORY':
            case 'INCENTIVE':
            case 'SOR': {
                builtCodes = [code];
                break;
            }

            case 'BUNDLE': {
                const parentCode = { ...code, quantity: code?.quantity ?? 1, childCodes: [], type: 'BUNDLE', data: { beforeId: code?._id } } as CatalogueCode;
                const childCodes = CodeUtility.createBundleChildCodes(code);
                builtCodes = [...builtCodes, { ...parentCode }, ...childCodes];
                break;
            }

            case 'KIT': {
                const parentCode = { ...code, quantity: code?.quantity ?? 1, childCodes: [], type: 'KIT', data: { beforeId: code?._id } } as CatalogueCode;
                const childCodes = CodeUtility.createKitChildCodes(code);
                builtCodes = [...builtCodes, { ...parentCode }, ...(childCodes ?? [])] as CatalogueCode[];
                break;
            }
        }

        return CodeUtility.validateUploadedCodes(builtCodes, excludeCancelled);
    }

    public static validateUploadedCodes(builtCodes: EmbeddedCode[], excludeCancelled: boolean = true): EmbeddedCode[] {

        const builtCodesValid = Enumerable
            .from(builtCodes)
            .select(x => {
                //Do it in one select so we dont lose the
                //extra fields from code that is passed in

                //Not sure why we need to do this, but it's here
                //This change broke a bunch of stuff and this is the second attempt to fix it
                //previously, we never made the call to ToEmbeddedCode
                const catalogueCode = ToEmbeddedCode(x);
                const catalogueCodeId = catalogueCode._id;
                const item = {
                    ...catalogueCode,
                    ...x,
                    _id: v4(),
                    status: x.status ?? CodeStatus.Normal,
                    customBuyPrice: x.customBuyPrice ?? false,
                    customSellPrice: x.customSellPrice ?? false,
                    buyPrice: x.buyPrice ?? 0,
                    sellPrice: x.sellPrice ?? 0,
                    quantity: x.quantity ?? 1,
                    location: x.location,
                    note: x.note,
                    // codeUoM: x.codeUoM ?? ,
                    catalogueCodeId,
                };
                return item;
            })
            .toArray();
        //Allow cancelled codes to come through, because some awesome AGS integration
        //will send cancelled codes on creation and they need to be seen :\
        const validItems = WorkOrderUtility.sortCodes(builtCodesValid, excludeCancelled);
        return validItems;
    }

    public static getChildcodes(code: CatalogueCode): EmbeddedCode[] {
        let childCodes: EmbeddedCode[] = [];
        switch (code.type) {

            case 'BUNDLE': {
                childCodes = CodeUtility.createBundleChildCodes(code);
                break;
            }
            case 'KIT': {
                childCodes = CodeUtility.createKitChildCodes(code);
                break;
            }
            case 'SERVICE':
            case 'INVENTORY':
            case 'NON-INVENTORY':
            case 'INCENTIVE':
            default: {
                childCodes = [];
                break;
            }
        }
        return childCodes;
    }

    private static createKitChildCodes(code: CatalogueCode): EmbeddedCode[] {
        let childCodes = [...(code?.childCodes ?? [])] as CatalogueCode[];
        const parentCode = { ...code, quantity: code?.quantity ?? 1, childCodes: [], type: 'KIT', data: { beforeId: code?._id } } as CatalogueCode;

        childCodes = Enumerable
            .from(childCodes)
            .select(x => {
                const safeChildQuantity = x?.quantity ?? 1;
                const childQuantity = safeChildQuantity * parentCode.quantity;

                return {
                    ...x,
                    parentType: 'KIT',
                    quantity: childQuantity,
                    data: { parentCodeId: parentCode._id }
                };
            })
            .toArray() as CatalogueCode[];

        return childCodes;
    }

    private static createBundleChildCodes(code: CatalogueCode): EmbeddedCode[] {
        let childCodes = [...(code?.childCodes ?? [])] as CatalogueCode[];
        const parentCode = { ...code, quantity: code?.quantity ?? 1, childCodes: [], type: 'BUNDLE', data: { beforeId: code?._id } } as CatalogueCode;

        childCodes = Enumerable
            .from(childCodes)
            .select(x => {

                const safeChildQuantity = x?.quantity ?? 1;
                const childQuantity = safeChildQuantity * parentCode.quantity;

                return {
                    ...x,
                    parentType: 'BUNDLE',
                    quantity: childQuantity,
                    data: { parentCodeId: parentCode._id }
                } as CatalogueCode;
            })
            .toArray();
        return childCodes;
    }

    public static isExcludedForSellPrice(code: EmbeddedCode): boolean {
        if (!code) return false;
        return (!code.parentType || code.parentType !== 'BUNDLE');
    }

    public static isExcludedForBuyPrice(code: EmbeddedCode): boolean {
        if (!code) return false;
        return (!code.type || code.type !== 'BUNDLE');
    }

    public static addSourceToCodes(codes: EmbeddedCode[], source: Source): EmbeddedCode[] {

        return Enumerable.from(codes)
            .select(x => {

                if (!x['source']) {
                    x['source'] = source;
                }

                return x;
            })
            .toArray();
    }

    // HUY Le: When update the logic in this method, please update the method getTotalOneCodeInfo as well. 
    // I was scare to update this method because, so I create the getTotalOneCodeInfo to calculate the subTotalPrice of one code.
    public static getTotalCodesInfo(codes: EmbeddedCode[], appliedUoM = false): TotalCodeInfo {

        if (!codes?.length)
            return { totalSell: 0, totalBuy: 0, totalCodes: 0 } as TotalCodeInfo;

        const safeCodes = Enumerable.from(codes)
            .where(x => !!x?._id)
            .where(x => x.status !== CodeStatus.Cancelled && x.type !== 'INCENTIVE');

        const totalSell = safeCodes
            .select(x => {

                const qty = x?.quantity ?? 1;

                const price = new Decimal(x?.sellPrice ?? 0).toDP(4).toNumber();

                const conversionRate = x?.codeUoM?.conversionRate ?? 1;
                const lineSellPrice = appliedUoM
                    ? qty * price
                    : qty * conversionRate * price;

                const round = new Decimal(lineSellPrice ?? 0).toDP(2).toNumber();

                return round;
            })
            .sum();


        const totalBuy = safeCodes
            .select(x => {

                const qty = x?.quantity ?? 1;

                const price = new Decimal(x?.buyPrice ?? 0).toDP(4).toNumber();
                const conversionRate = x?.codeUoM?.conversionRate ?? 1;
                const lineBuyPrice = appliedUoM
                    ? qty * price
                    : qty * conversionRate * price;


                const round = new Decimal(lineBuyPrice ?? 0).toDP(2).toNumber();

                return round;
            })
            .sum();

        const info: TotalCodeInfo = { totalSell, totalBuy, totalCodes: codes?.length ?? 0 };

        return info;
    }

    public static getTotalOneCodeInfo(code: EmbeddedCode, appliedUoM = false): TotalCodeInfo {

        if (!code) {
            return { totalSell: 0, totalBuy: 0, totalCodes: 0 } as TotalCodeInfo;
        }

        const conversionRate = code?.codeUoM?.conversionRate ?? 1;
        const qty = code?.quantity ?? 1;

        const sellPrice = new Decimal(code?.sellPrice ?? 0).toDP(4).toNumber();
        const lineSellPrice = appliedUoM
            ? qty * sellPrice
            : qty * conversionRate * sellPrice;
        const totalSell = new Decimal(lineSellPrice ?? 0).toDP(2).toNumber();

        const buyPrice = new Decimal(code?.buyPrice ?? 0).toDP(4).toNumber();
        const lineBuyPrice = appliedUoM
            ? qty * buyPrice
            : qty * conversionRate * buyPrice;
        const totalBuy = new Decimal(lineBuyPrice ?? 0).toDP(2).toNumber();

        const info: TotalCodeInfo = { totalSell, totalBuy, totalCodes: 1 };
        return info;
    }

    /**
     * Extended line buy/sell for a single code: quantity × unit price (2dp) — the per-row figure shown
     * as Ext Buy / Ext Sell. Excludes UoM conversionRate (appliedUoM), matching the items table's
     * per-line extension. Single source of truth for that math (delegates to getTotalOneCodeInfo).
     */
    public static getExtBuy(code: EmbeddedCode): number {

        return this.getTotalOneCodeInfo(code, true).totalBuy;
    }

    public static getExtSell(code: EmbeddedCode): number {

        return this.getTotalOneCodeInfo(code, true).totalSell;
    }

    public static getClaimItemsTotal(items: ClaimItem[]): number {

        if (!items?.length)
            return 0;

        const total = Enumerable.from(items)
            .where(x => !!x?._id)
            .where(x => x.status !== CodeStatus.Cancelled && x.type !== 'INCENTIVE')
            .select(x => {

                const qty = x?.quantity ?? 1;
                const price = x?.price ?? 0;

                /* the price already include conversionRate. so don't need to calculate again.
                //const conversionRate = x?.codeUoM?.conversionRate ?? 1;
                //const lineSellPrice = qty * conversionRate * price;
                */
                const lineSellPrice = new Decimal(qty).mul(price).toNumber();
                const round = new Decimal(lineSellPrice ?? 0).toDP(2).toNumber();

                return round;
            })
            .sum();

        return total;
    }

    public static filterClaimItems(items: ClaimItem[], filter: string): ClaimItem[] {

        if (!filter) return items;

        const searchRegex = new RegExp(filter, 'i');

        const filteredItems = items.filter(item => {
            const propertiesToSearch = [
                item.name,
                item.description,
                item.note,
                item.status,
                item.price?.toString(),
                item.category?.name,
                item.category?.subCategoryName
            ].filter(Boolean); // Remove undefined/null properties

            return propertiesToSearch.some(property => searchRegex.test(property));
        });

        return filteredItems;
    }


    public static getLabourCode(timesheets: Partial<TimesheetEntry>[], finance: FinanceSettings): EmbeddedCode {

        if (!timesheets?.length)
            return null;

        const totalSell = Enumerable.from(timesheets).select(x => x.total?.amount).sum();
        const timesheetIds = Enumerable.from(timesheets)
            .select(x => x?._id)
            .toArray();

        const { labour, defaultUoM } = finance;

        const code = {
            _id: LabourCodeId,
            name: labour?.name,
            description: 'Not Set',
            buyPrice: 0,
            sellPrice: totalSell,
            customBuyPrice: true,
            customSellPrice: true,
            codeInvoicingStatus: CodeInvoiceStatus.Billable,
            active: true,
            uom: defaultUoM?.name,
            codeUoM: {
                _id: defaultUoM?._id,
                uom: defaultUoM,
                conversionRate: 1
            },
            quantity: 1,
            type: 'LABOUR',
            status: CodeStatus.Normal,
            timesheetIds: timesheetIds
        } as EmbeddedCode;


        return code;
    }

    public static getPrepaymentInvoiceCode(setting: PrepaymentSetting, name: string, description: string, totalSell: number): EmbeddedCode {

        const code: EmbeddedCode = {
            _id: setting?._id || PrepaymentCodeId || v4(),
            name: name || setting?.name || PrepaymentCodeName,
            description,
            buyPrice: 0,
            sellPrice: totalSell,
            customBuyPrice: true,
            customSellPrice: true,
            codeInvoicingStatus: CodeInvoiceStatus.Billable,
            active: true,
            quantity: 1,
            type: 'LABOUR',
            status: undefined,
            category: undefined
        };



        return code;
    }

}
